import type { Note } from '@/types/note';
import { PERSIST_DEBOUNCE_MS } from '@/constants/notes';

export interface WriteBackSchedulerOptions {
  readonly delayMs?: number;
  readonly flush: (notes: readonly Note[]) => void;
  readonly getAllNotes: () => readonly Note[];
  readonly registerUnloadHandler?: (fn: () => void) => () => void;
}

/**
 * Coalesces rapid mutations into a single persistence write.
 *
 * - `markDirty()` starts a debounce window; further calls reset it.
 * - `flushNow()` writes immediately (exposed to tests and to a manual Sync button).
 * - Unload guarantee: subscribes to `beforeunload` so a tab close still writes.
 */
export class WriteBackScheduler {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private dirty = false;
  private readonly disposeUnload: (() => void) | null = null;
  private readonly delayMs: number;

  constructor(private readonly opts: WriteBackSchedulerOptions) {
    this.delayMs = opts.delayMs ?? PERSIST_DEBOUNCE_MS;
    const register = opts.registerUnloadHandler ?? defaultRegisterUnload;
    this.disposeUnload = register(() => this.flushNow());
  }

  markDirty(): void {
    this.dirty = true;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flushNow(), this.delayMs);
  }

  flushNow(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.dirty) return;
    this.dirty = false;
    this.opts.flush(this.opts.getAllNotes());
  }

  dispose(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.disposeUnload?.();
  }
}

function defaultRegisterUnload(fn: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => fn();
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}
